import { api } from '@shop/api-client'
import type {
  CashSession,
  AttendanceRow,
  CashPayout,
  CashSessionReport,
  CustomerCreated,
  CustomerSummary,
  PaymentRequest,
  PosDashboard,
  PosHit,
  ReturnableLine,
  SaleDetail,
  SaleLineRequest,
  SaleReceipt,
  SoldItem,
  SalesReturnDetail,
  InvoiceWithItems,
  PayoutCategory,
  PayoutOptions,
} from './contracts'

/**
 * نداءاتُ الصندوق — <b>نقلٌ لا منطق</b>.
 *
 * ولا سطرَ حسابٍ هنا: لا مجموعَ سلّةٍ يُحسب، ولا فكّةَ، ولا فحصَ رصيد. كلُّ
 * ذلك يردّه الخادمُ في جواب كلّ نداء — <b>والشاشةُ تعرض ما وصل</b>.
 *
 * ⚠ <b>ولماذا يُعاد رسمُ السلّة من الجواب بعد كلّ تعديل:</b> المجموعُ والضريبةُ
 *   والخصمُ تُحسب في الخادم بالتقريب نفسِه الذي يُحفظ به. وحسابُ نسخةٍ في
 *   المتصفّح يُنتج فرقَ فلسٍ يراه الزبونُ على الشاشة ولا يراه على الفاتورة.
 */

// ── الورديّة ──────────────────────────────────────────────────────────────

export function currentShift(branchId: string, signal?: AbortSignal) {
  // ⚠ و٤٠٤ هنا **جوابٌ لا عطب**: «لا ورديّةَ مفتوحة» حالةٌ عاديّةٌ في أوّل
  //   اليوم. فتُترجَم إلى `null` ولا تُعرض بانرَ خطأٍ أحمرَ في وجه الكاشير.
  return api<CashSession | null>('/api/v1/cash-sessions/current?branchId=' + branchId, { signal })
    .catch((failure: unknown) => {
      if ((failure as { status?: number }).status === 404) {
        return null
      }

      throw failure
    })
}

export function openShift(branchId: string, openingCash: number, notes?: string) {
  return api<CashSession>('/api/v1/cash-sessions', {
    method: 'POST',
    body: { branchId, openingCash, notes },
  })
}

export function shiftReport(sessionId: string, signal?: AbortSignal) {
  return api<CashSessionReport>(`/api/v1/cash-sessions/${sessionId}/report`, { signal })
}

export function payoutOptions(signal?: AbortSignal) {
  return api<PayoutOptions>('/api/v1/cash-payouts/options', { signal })
}

export function createPayout(
  sessionId: string,
  input: { category: PayoutCategory; amount: number; partyId?: string | null; memo?: string | null },
) {
  return api<CashPayout>(`/api/v1/cash-sessions/${sessionId}/payouts`, { method: 'POST', body: input })
}

export function voidPayout(payoutId: string, reason: string) {
  return api<CashPayout>(`/api/v1/cash-payouts/${payoutId}/void`, { method: 'POST', body: { reason } })
}

export function attendanceToday(signal?: AbortSignal) {
  return api<AttendanceRow[]>('/api/v1/attendance', { signal })
}

export function punch(userId: string, way: 'in' | 'out') {
  return api<AttendanceRow>(`/api/v1/attendance/punch-${way}`, { method: 'POST', body: { userId } })
}

export function closeShift(sessionId: string, countedCash: number, reason?: string) {
  return api<CashSession>(`/api/v1/cash-sessions/${sessionId}/close`, {
    method: 'POST',
    body: { countedCash, reason },
  })
}

// ── البحث ────────────────────────────────────────────────────────────────

export function lookup(
  branchId: string,
  warehouseId: string,
  q: string,
  signal?: AbortSignal,
) {
  const query = new URLSearchParams({ branchId, warehouseId, q, limit: '20' })

  return api<PosHit[]>(`/api/v1/pos/lookup?${query}`, { signal })
}

// ── البيع ────────────────────────────────────────────────────────────────

/**
 * مسحةٌ واحدةٌ = <b>رحلةٌ واحدة</b>.
 *
 * ⚠ **وهذا تسريعٌ مقيس**: معالجةُ الخادم ٩–٢٣ مللي ثانية، والرحلةُ عبر
 *   الإنترنت ٢٣٠ — فالوقتُ كلُّه شبكةٌ لا حساب. وكانت المسحةُ تكلّف بحثًا ثمّ
 *   إنشاءَ بيعةٍ ثمّ تثبيتَ سطورٍ ≈ ٧٠٠ مللي ثانية.
 */
export function scan(input: {
  branchId: string
  warehouseId: string
  code: string
  saleId?: string
  qty?: number
}) {
  return api<SaleDetail>('/api/v1/pos/scan', { method: 'POST', body: input })
}

export function createSale(input: {
  branchId: string
  warehouseId: string
  customerId?: string | null
  customerName?: string | null
  customerPhone?: string | null
  holdLabel?: string | null
}) {
  return api<SaleDetail>('/api/v1/sales', { method: 'POST', body: input })
}

export function setLines(saleId: string, lines: SaleLineRequest[]) {
  return api<SaleDetail>(`/api/v1/sales/${saleId}/lines`, { method: 'PUT', body: { lines } })
}

export function completeSale(saleId: string, payments: PaymentRequest[]) {
  return api<SaleDetail>(`/api/v1/sales/${saleId}/complete`, { method: 'POST', body: { payments } })
}

export function cancelSale(saleId: string, reason: string) {
  return api<SaleDetail>(`/api/v1/sales/${saleId}/cancel`, { method: 'POST', body: { reason } })
}

export function findSale(saleId: string, signal?: AbortSignal) {
  return api<SaleDetail>(`/api/v1/sales/${saleId}`, { signal })
}

/**
 * مبيعاتُ ورديّةٍ بعينها — <b>لا مبيعاتُ اليوم</b>.
 *
 * والورديّةُ قد تمتدّ إلى ما بعد منتصف الليل، واليومُ قد يحمل ورديّتين —
 * فالكاشيرُ الذي يسأل «كم بعتُ؟» يقصد ورديّتَه التي بين يديه.
 */
export function shiftSales(cashSessionId: string, signal?: AbortSignal) {
  return api<SaleDetail['header'][]>(
    `/api/v1/sales?status=COMPLETED&take=200&cashSessionId=${cashSessionId}`,
    { signal },
  )
}

/** ما بِيع في الورديّة مُجمَّعًا بالصنف — «شو بعتُ؟». */
/**
 * فواتيرُ الورديّة **بموادّها وطرقِ دفعها — في رحلةٍ واحدة**.
 *
 * ⚠ ولا تفاصيلُ كلِّ فاتورةٍ بطلب: الرحلةُ ٢٣٠ مللي ثانية، وعشرون فاتورةً
 *   كانت ستعني خمسَ ثوانٍ يقف فيها الكاشير.
 */
export function shiftInvoices(cashSessionId: string, signal?: AbortSignal) {
  return api<{ count: number; total: number; rows: InvoiceWithItems[] }>(
    `/api/v1/sales/shift-invoices?cashSessionId=${cashSessionId}`, { signal })
}

export function shiftItems(cashSessionId: string, signal?: AbortSignal) {
  return api<SoldItem[]>(`/api/v1/sales/shift-items?cashSessionId=${cashSessionId}`, { signal })
}

export function listSales(status: string, q?: string, signal?: AbortSignal) {
  // ⚠ **والبحثُ في الخادم لا في الجهاز**: التصفيةُ المحلّيّةُ كانت تبحث في
  //   آخر ثلاثين فاتورةً فقط، ففاتورةُ الأمسِ لا تُوجَد مهما كُتب.
  const term = q?.trim() ? `&q=${encodeURIComponent(q.trim())}` : ''

  return api<SaleDetail['header'][]>(
    `/api/v1/sales?status=${status}&take=30${term}`, { signal })
}

export function receipt(saleId: string, signal?: AbortSignal) {
  return api<SaleReceipt>(`/api/v1/sales/${saleId}/receipt`, { signal })
}

// ── المرتجع وردُّ المال ──────────────────────────────────────────────────

export function returnable(saleId: string, signal?: AbortSignal) {
  return api<ReturnableLine[]>(`/api/v1/sales/${saleId}/returnable`, { signal })
}

export function createReturn(saleId: string, kind: 'CUSTOMER_RETURN' | 'VOID', reason?: string) {
  return api<SalesReturnDetail>('/api/v1/sales-returns', {
    method: 'POST',
    body: { saleId, returnKind: kind, reason },
  })
}

export function setReturnLines(
  returnId: string,
  lines: { saleLineId: string; qty: number; reasonCode?: string }[],
) {
  return api<SalesReturnDetail>(`/api/v1/sales-returns/${returnId}/lines`, {
    method: 'PUT',
    body: { lines },
  })
}

/** <b>والمالُ اختياريٌّ هنا</b>: فارغٌ ⇒ البضاعةُ تعود والمالُ ينتظر المشرف. */
export function postReturn(returnId: string, refunds: PaymentRequest[]) {
  return api<SalesReturnDetail>(`/api/v1/sales-returns/${returnId}/post`, {
    method: 'POST',
    body: { refunds },
  })
}

export function refundReturn(returnId: string, refunds: PaymentRequest[]) {
  return api<SalesReturnDetail>(`/api/v1/sales-returns/${returnId}/refund`, {
    method: 'POST',
    body: { refunds },
  })
}

export function listReturns(status: string, signal?: AbortSignal) {
  return api<SalesReturnDetail['header'][]>(`/api/v1/sales-returns?status=${status}&take=30`, { signal })
}

// ── العميل ───────────────────────────────────────────────────────────────

export function findCustomerByPhone(phone: string, signal?: AbortSignal) {
  return api<CustomerSummary | null>(`/api/v1/customers/by-phone/${encodeURIComponent(phone)}`, { signal })
    .catch((failure: unknown) => {
      if ((failure as { status?: number }).status === 404) {
        return null
      }

      throw failure
    })
}

export function createCustomer(nameAr: string, phone: string) {
  return api<CustomerCreated>('/api/v1/customers', { method: 'POST', body: { nameAr, phone } })
}

// ── اللوحة ───────────────────────────────────────────────────────────────

export function dashboard(signal?: AbortSignal) {
  return api<PosDashboard>('/api/v1/sales-dashboard', { signal })
}
